import { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { useCurrentCompany } from "../components/CompanySwitcher";
import { fmtMoney, fmtDate } from "../lib/format";

interface ClientRow {
  id: number; name: string; email: string | null; language: "en" | "th";
  credit_terms_days: number; open_count: number; open_total: number;
  last_sent_at: string | null;
}

interface SendResult {
  client_id: number;
  client_name: string;
  status: "sent" | "failed" | "skipped";
  provider_id?: string;
  error?: string;
}

export default function Send() {
  const { current } = useCurrentCompany();
  const [rows, setRows] = useState<ClientRow[]>([]);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [previewing, setPreviewing] = useState<{ html: string; subject: string; to: string | null; lang: string } | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [dryRun, setDryRun] = useState(false);
  const [results, setResults] = useState<SendResult[] | null>(null);

  useEffect(() => {
    if (!current) return;
    api<{ clients: ClientRow[] }>(`/api/clients?company_id=${current.id}`)
      .then((r) => setRows(r.clients.filter((c) => c.open_count > 0)));
  }, [current?.id]);

  const eligible = useMemo(() => rows.filter((r) => r.email), [rows]);
  const allSelected = eligible.length > 0 && eligible.every((r) => selected.has(r.id));
  function toggleAll() {
    if (allSelected) setSelected(new Set());
    else setSelected(new Set(eligible.map((r) => r.id)));
  }
  function toggle(id: number) {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  }

  async function preview(clientId: number) {
    setPreviewLoading(true);
    try {
      const r = await api<{ subject: string; html: string; to: string | null; language: string }>(
        "/api/preview", { method: "POST", body: JSON.stringify({ client_id: clientId }) },
      );
      setPreviewing({ subject: r.subject, html: r.html, to: r.to, lang: r.language });
    } catch (e) {
      alert(e instanceof Error ? e.message : "Preview failed");
    } finally { setPreviewLoading(false); }
  }

  async function send() {
    if (!selected.size) return;
    if (!dryRun) {
      const companyName = current?.name || "(unknown company)";
      const msg = `Send ${selected.size} reminder email${selected.size > 1 ? "s" : ""} FROM\n\n  "${companyName}"\n\nto the selected clients?\n\n(Each will go via finance@email.mailbit.co.th. admin@mailbit.co.th is CC'd.)`;
      if (!confirm(msg)) return;
    }
    setSending(true); setResults(null);
    try {
      const r = await api<{ results: SendResult[] }>("/api/send", {
        method: "POST",
        body: JSON.stringify({ client_ids: Array.from(selected), dry_run: dryRun }),
      });
      setResults(r.results);
      setSelected(new Set());
      // refresh last_sent_at
      if (current) {
        const refreshed = await api<{ clients: ClientRow[] }>(`/api/clients?company_id=${current.id}`);
        setRows(refreshed.clients.filter((c) => c.open_count > 0));
      }
    } catch (e) {
      alert(e instanceof Error ? e.message : "Send failed");
    } finally {
      setSending(false);
    }
  }

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-2xl font-semibold tracking-tightish text-slate-900">Send reminders</h1>
        <p className="mt-1 text-sm text-slate-500">
          Acting as <span className="font-semibold text-indigo-700">{current?.name || "—"}</span>
          {" · "}reminders go from <span className="font-mono">finance@email.mailbit.co.th</span>, CC <span className="font-mono">admin@mailbit.co.th</span>
        </p>
      </div>

      <div className="card p-4">
        <div className="flex flex-wrap items-center gap-3">
          <div className="section-title">Open queue</div>
          <div className="text-sm text-slate-500">{selected.size} of {eligible.length} selected</div>
          <label className="ml-2 flex items-center gap-1.5 text-sm">
            <input type="checkbox" checked={dryRun} onChange={(e) => setDryRun(e.target.checked)} />
            Dry run (no email sent)
          </label>
          <div className="ml-auto flex items-center gap-2">
            <button className="btn-secondary" onClick={toggleAll}>{allSelected ? "Clear" : "Select all"}</button>
            <button className="btn-primary" onClick={send} disabled={!selected.size || sending}>
              {sending ? "Sending…" : (dryRun ? `Dry run → ${selected.size}` : `Send to ${selected.size}`)}
            </button>
          </div>
        </div>
      </div>

      <div className="card overflow-hidden">
        <table className="refined">
          <thead>
            <tr>
              <th className="w-8 pl-3 pr-0"><input type="checkbox" checked={allSelected} onChange={toggleAll} /></th>
              <th>Client</th>
              <th>Email</th>
              <th className="w-16">Lang</th>
              <th className="w-16">Open</th>
              <th className="w-36 text-right pr-8">Total</th>
              <th className="w-36">Last sent</th>
              <th className="w-24"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((c) => {
              const disabled = !c.email;
              return (
                <tr key={c.id}>
                  <td className="pl-3 pr-0">
                    <input type="checkbox" disabled={disabled}
                           checked={selected.has(c.id)} onChange={() => toggle(c.id)} />
                  </td>
                  <td className="font-medium text-slate-900">{c.name}</td>
                  <td>{c.email
                    ? <span className="text-slate-700">{c.email}</span>
                    : <span className="text-xs text-rose-600">missing — add on Clients tab</span>}</td>
                  <td><span className="pill pill-slate uppercase">{c.language}</span></td>
                  <td className="text-slate-700">{c.open_count}</td>
                  <td className="text-right pr-8 font-mono font-semibold text-slate-900">{fmtMoney(c.open_total, current?.currency || "THB")}</td>
                  <td className="text-xs text-slate-500">{c.last_sent_at ? fmtDate(c.last_sent_at) : "—"}</td>
                  <td className="pr-3 text-right">
                    <button className="btn-secondary !py-1 !px-2.5 text-xs" disabled={disabled || previewLoading}
                            onClick={() => preview(c.id)}>Preview</button>
                  </td>
                </tr>
              );
            })}
            {!rows.length && (
              <tr><td colSpan={8} className="px-3 py-12 text-center text-slate-500">
                No clients with open invoices.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      {previewing && (
        <div className="fixed inset-0 z-10 flex items-center justify-center bg-slate-900/40 p-4"
             onClick={() => setPreviewing(null)}>
          <div className="card max-h-[90vh] w-full max-w-2xl overflow-y-auto p-5"
               onClick={(e) => e.stopPropagation()}>
            <div className="text-xs uppercase text-slate-500">To · {previewing.lang.toUpperCase()}</div>
            <div className="font-mono text-sm">{previewing.to}</div>
            <div className="mt-3 text-xs uppercase text-slate-500">Subject</div>
            <div className="font-semibold">{previewing.subject}</div>
            <div className="mt-3 rounded-md border border-slate-200 p-4"
                 dangerouslySetInnerHTML={{ __html: previewing.html }} />
            <div className="mt-4 text-right">
              <button className="btn-secondary" onClick={() => setPreviewing(null)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {results && (
        <div className="card p-4">
          <div className="font-semibold">Result</div>
          <ul className="mt-2 space-y-1 text-sm">
            {results.map((r) => (
              <li key={r.client_id} className="flex items-center gap-2">
                <span className={`rounded px-1.5 py-0.5 text-xs ${
                  r.status === "sent" ? "bg-emerald-100 text-emerald-800"
                  : r.status === "failed" ? "bg-rose-100 text-rose-800"
                  : "bg-amber-100 text-amber-800"}`}>{r.status}</span>
                <span>{r.client_name}</span>
                {r.error && <span className="text-rose-700">— {r.error}</span>}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
